import { useState } from "react";
import {
  procureiqDisplaySupplierName,
  procureiqColorFromName,
  procureiqSupplierInitial,
  procureiqSupplierCutoffLabel,
  procureiqSupplierInfoTitle,
  procureiqSupplierLiveTotal,
  procureiqSupplierIsBelowMin,
  procureiqEffectiveMinOrderEur,
  procureiqFormatStockDisplay,
  procureiqFormatQtyWithUnit,
  procureiqAvgWindowQty,
  procureiqBaseUnit,
  procureiqCompareBySoonestStockout,
  procureiqContentsUnit,
  procureiqPackageMenuLabel,
  procureiqEditedLineValue,
  formatProcureEuro,
  formatProcureEuroCompact,
} from "./helpers";

function lineIsInOrder(line, orderQtys) {
  const raw = orderQtys?.[line.product_id];
  if (raw === "" || raw == null) return false;
  const qty = Number(raw);
  return Number.isFinite(qty) && qty > 0;
}

function LineRow({
  line,
  orderQtys,
  t,
  onSetLineQty,
  onSelectPackage,
  onPackSuggestionAccept,
  onPackSuggestionDismiss,
  dismissedPackTips,
}) {
  const unit = line.forecast_unit || line.case_unit || "";
  const unitShort = procureiqBaseUnit(unit);
  const stockDisplay = procureiqFormatStockDisplay(line.estimated_stock, unit);
  const suggestedQty = line.suggested_order_qty;
  const avgQty = procureiqAvgWindowQty(line);
  const orderQtyRaw = orderQtys[line.product_id];
  const orderQtyDisplay =
    orderQtyRaw === "" || orderQtyRaw == null ? "" : String(orderQtyRaw);
  const orderQtyNum = orderQtyRaw === "" || orderQtyRaw == null ? null : Number(orderQtyRaw);
  const orderByPack = Number(line.units_per_pack) > 1;
  const tipKey = `${line.product_id}`;
  const tipDismissed = Boolean(dismissedPackTips?.[tipKey]);
  const packTip = !tipDismissed
    ? (line.pack_suggestions || []).find(
        (s) => s && s.reason !== "blocked_fresh" && Number(s.save_eur) > 0
      )
    : null;
  const step = orderByPack
    ? 1
    : Number(line.case_size) > 0
      ? Number(line.case_size)
      : 1;
  const rawPackUnit = String(line.order_unit || "").trim();
  const packUnit = rawPackUnit && !/^[×(]/.test(rawPackUnit) ? rawPackUnit : "";
  const orderUnitLabel = orderByPack ? packUnit : unitShort;
  const isBookStock = line.stock_source === "ledger_book";
  const statusClass = line.needs_stock_count
    ? "danger"
    : isBookStock
      ? "warn"
      : suggestedQty == null
        ? "warn"
        : "ok";
  const statusIcon = line.needs_stock_count
    ? "🔴"
    : isBookStock
      ? "◐"
      : suggestedQty == null
        ? "⚠"
        : "✓";
  const selectedPackage = (line.packages || []).find(
    (pkg) => pkg.packaging_id === line.selected_package_id
  ) || (line.packages || [])[0];
  const packCount = Math.round(Number(suggestedQty));
  const unitsPerPack = Number(selectedPackage?.units_per_pack || line.units_per_pack);
  const packUnitName = String(selectedPackage?.unit_name || packUnit || "").trim();
  const packSizeLabel = Number.isFinite(unitsPerPack)
    ? (Math.abs(unitsPerPack - Math.round(unitsPerPack)) < 1e-6
      ? String(Math.round(unitsPerPack))
      : String(unitsPerPack))
    : "";
  const contentsUnit = procureiqContentsUnit(
    selectedPackage?.baseunit_name || (orderByPack ? "Piece" : unit),
    unit
  );
  const contentsLabel = contentsUnit;
  const suggestToken = packUnitName || packSizeLabel;
  const suggestLabel =
    suggestedQty == null
      ? "—"
      : orderByPack
        ? `${packCount} * ${suggestToken}`.trim()
        : procureiqFormatQtyWithUnit(suggestedQty, unit);
  const orderTotalLabel = Number.isFinite(orderQtyNum) && Number.isFinite(unitsPerPack)
    ? (Math.abs(orderQtyNum * unitsPerPack - Math.round(orderQtyNum * unitsPerPack)) < 1e-6
      ? String(Math.round(orderQtyNum * unitsPerPack))
      : String(Math.round(orderQtyNum * unitsPerPack * 1000) / 1000))
    : "";
  const avgLabel = avgQty != null ? procureiqFormatQtyWithUnit(avgQty, unit) : "—";
  const unitCost = Number(line.unit_cost);
  const hasUnitCost = Number.isFinite(unitCost) && unitCost > 0;
  const lineTotal = procureiqEditedLineValue(line, orderQtys);
  const eachLabel = orderByPack
    ? t("per pack", "ανά συσκευασία")
    : orderUnitLabel || t("each", "τεμ.");
  const inOrder = Number.isFinite(orderQtyNum) && orderQtyNum > 0;

  return (
    <div className={`procureiq-line-item${inOrder ? " is-in-order" : ""}`}>
      <span
        className={`procureiq-line-status ${statusClass}`}
        title={
          line.needs_stock_count
            ? t("Needs stock count", "Χρειάζεται καταμέτρηση")
            : isBookStock
              ? t("Using Supy book stock", "Απόθεμα από Supy ledger")
              : suggestedQty == null
                ? t("Suggestion unavailable", "Μη διαθέσιμη πρόταση")
                : t("Looks normal", "Φυσιολογικό")
        }
      >
        {statusIcon}
      </span>
      <div className="procureiq-line-main">
        <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text-primary)" }}>
          {line.item_name || line.product_name}
        </div>
        {Array.isArray(line.packages) && line.packages.length > 0 && (
          <label className="procureiq-pack-picker" onClick={(e) => e.stopPropagation()}>
            <span>{t("Package", "Συσκευασία")}</span>
            <select
              value={line.selected_package_id || ""}
              onChange={(e) => onSelectPackage(line.product_id, e.target.value)}
            >
              {line.packages.map((pkg) => (
                <option key={pkg.packaging_id} value={pkg.packaging_id}>
                  {procureiqPackageMenuLabel(pkg)} · {formatProcureEuro(pkg.unit_price)}
                </option>
              ))}
            </select>
          </label>
        )}
        {packTip && (
          <div
            className="procureiq-pack-tip"
            style={{
              marginTop: 6,
              padding: "6px 8px",
              borderRadius: 6,
              background: "rgba(34, 197, 94, 0.12)",
              border: "1px solid rgba(34, 197, 94, 0.35)",
              fontSize: 11,
              color: "var(--text-primary)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {packTip.message
                || t(
                  "Larger pack may save money over the use horizon",
                  "Μεγαλύτερη συσκευασία μπορεί να εξοικονομήσει στο ορίζοντα χρήσης"
                )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-sm"
                style={{ fontSize: 11, padding: "2px 8px" }}
                onClick={() =>
                  onPackSuggestionAccept?.(line, packTip)
                }
              >
                {t("Use this pack", "Χρήση συσκευασίας")}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                style={{ fontSize: 11, padding: "2px 8px", opacity: 0.85 }}
                onClick={() => onPackSuggestionDismiss?.(line, packTip)}
              >
                {t("Dismiss", "Απόρριψη")}
              </button>
            </div>
          </div>
        )}
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 1 }}>
          {stockDisplay != null
            ? `${t("stock", "απόθεμα")} ${stockDisplay}`
            : `${t("stock", "απόθεμα")} —`}
          {" · "}
          {procureiqFormatQtyWithUnit(line.predicted_window_qty, unit, { decimals: 1 })}{" "}
          {t("predicted over", "πρόβλεψη σε")} {line.days_window}d
        </div>
        {hasUnitCost && (
          <div style={{ fontSize: 11, color: "var(--text-primary)", marginTop: 3, fontWeight: 700 }}>
            {formatProcureEuro(unitCost)} {eachLabel}
            {" · "}
            {t("total", "σύνολο")} {formatProcureEuro(lineTotal)}
          </div>
        )}
        {line.needs_stock_count && (
          <div style={{ fontSize: 10, color: "#991B1B", marginTop: 2, fontWeight: 600 }}>
            {t("Needs stock count", "Χρειάζεται καταμέτρηση")}
          </div>
        )}
        {!line.needs_stock_count && isBookStock && (
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2, fontWeight: 600 }}>
            {t("Book stock", "Απόθεμα ledger")}
          </div>
        )}
      </div>
      <div className="procureiq-line-metrics">
        <div
          className="procureiq-line-metric is-suggest"
          title={
            suggestedQty != null
              ? `${t("Model suggests", "Πρόταση μοντέλου")}: ${suggestLabel}`
              : t("No suggestion", "Χωρίς πρόταση")
          }
        >
          <div className="procureiq-line-metric-label">{t("Suggest", "Πρόταση")}</div>
          <div className="procureiq-line-metric-value">{suggestLabel}</div>
        </div>
        <div
          className="procureiq-line-metric"
          title={t(
            "4-week average consumption over the order window",
            "Μέση κατανάλωση 4 εβδομάδων στο παράθυρο παραγγελίας"
          )}
        >
          <div className="procureiq-line-metric-label">{t("Avg", "Μ.Ο.")}</div>
          <div className="procureiq-line-metric-value is-avg">{avgLabel}</div>
        </div>
        <div className="procureiq-line-order">
          <div className="procureiq-line-metric-label">{t("Order", "Παραγγ.")}</div>
          <div className="procureiq-order-input-wrap">
            <input
              type="number"
              min={0}
              step={step}
              className="procureiq-order-input"
              value={orderQtyDisplay}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const v = e.target.value;
                onSetLineQty(line.product_id, v === "" ? "" : v);
              }}
            />
            {orderByPack && packSizeLabel ? (
              <span className="procureiq-order-pieces">
                {orderTotalLabel
                  ? `*${packSizeLabel}${contentsLabel} = ${orderTotalLabel}${contentsLabel}`
                  : `*${packSizeLabel}${contentsLabel}`}
              </span>
            ) : Number.isFinite(orderQtyNum) ? (
              <span className="procureiq-order-pieces">
                {`= ${procureiqFormatQtyWithUnit(orderQtyNum, unit)}`}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="procureiq-line-remove"
          title={t("Remove", "Αφαίρεση")}
          onClick={(e) => {
            e.stopPropagation();
            onSetLineQty(line.product_id, 0);
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export function SupplierOrderCard({
  supplier,
  lang,
  t,
  expanded,
  orderQtys,
  isSending,
  actionBusy,
  onToggle,
  onSetLineQty,
  onSelectPackage,
  onPackSuggestionAccept,
  onPackSuggestionDismiss,
  dismissedPackTips,
  onReset,
  onOverrideMin,
}) {
  const [showNotInOrder, setShowNotInOrder] = useState(false);
  const isBelow = procureiqSupplierIsBelowMin(supplier, orderQtys);
  const supplierOrderTotal = procureiqSupplierLiveTotal(supplier, orderQtys);
  const minOrder = procureiqEffectiveMinOrderEur(supplier.min_order_eur);
  const minApplies = minOrder > 0;
  const lines = [...(supplier.lines || [])].sort(procureiqCompareBySoonestStockout);
  const notInOrderLines = lines.filter((line) => !lineIsInOrder(line, orderQtys));
  const inOrderCount = lines.length - notInOrderLines.length;
  const isEmptyOrder = inOrderCount === 0;
  const visibleLines = showNotInOrder
    ? lines
    : lines.filter((line) => lineIsInOrder(line, orderQtys));
  const itemCount = lines.length;
  const cutoffLabel = procureiqSupplierCutoffLabel(supplier);
  const displayName = procureiqDisplaySupplierName(supplier.supplier_name);
  const cutoffSuffix = cutoffLabel
    ? ` · ${t("order today by", "παραγγελία σήμερα έως")} ${cutoffLabel}`
    : "";

  return (
    <div className={`procureiq-supplier-card ${isBelow ? "is-below" : ""}`}>
      <div className="procureiq-supplier-row" onClick={onToggle}>
        <div
          className="procureiq-supplier-avatar"
          style={{ background: procureiqColorFromName(supplier.supplier_name) }}
        >
          {procureiqSupplierInitial(supplier.supplier_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              color: "var(--text-primary)",
            }}
          >
            {displayName}
            <button
              type="button"
              className="procureiq-info-btn"
              title={procureiqSupplierInfoTitle(supplier)}
              onClick={(e) => e.stopPropagation()}
            >
              ⓘ
            </button>
            {isSending && (
              <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
                {t("Sending…", "Αποστολή…")}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
            {itemCount} {itemCount === 1 ? t("item", "είδος") : t("items", "είδη")}
            {notInOrderLines.length > 0
              ? ` · ${inOrderCount} ${t("to order", "προς παραγγελία")}`
              : null}
            {cutoffSuffix}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {!isEmptyOrder ? (
            <div className="procureiq-bebas-eur">{formatProcureEuroCompact(supplierOrderTotal)}</div>
          ) : null}
          {minApplies && (
            <div className={`procureiq-min-eur${isBelow ? " is-below" : ""}`}>
              {t("min", "min")} {formatProcureEuroCompact(minOrder)}
            </div>
          )}
          <span
            className={`procureiq-badge ${
              isBelow
                ? "procureiq-badge-below"
                : isEmptyOrder
                  ? "procureiq-badge-empty"
                  : "procureiq-badge-ready"
            }`}
          >
            {isBelow
              ? t("⚠ Below min", "⚠ Κάτω min")
              : isEmptyOrder
                ? t("No order", "Χωρίς παραγγ.")
                : t("Ready", "Έτοιμο")}
          </span>
        </div>
        <span className={`procureiq-chevron ${expanded ? "is-open" : ""}`}>›</span>
      </div>

      {expanded && (
        <div className="procureiq-expanded-lines">
          {visibleLines.map((line) => (
            <LineRow
              key={line.product_id}
              line={line}
              orderQtys={orderQtys}
              t={t}
              onSetLineQty={onSetLineQty}
              onSelectPackage={onSelectPackage}
              onPackSuggestionAccept={onPackSuggestionAccept}
              onPackSuggestionDismiss={onPackSuggestionDismiss}
              dismissedPackTips={dismissedPackTips}
            />
          ))}

          {inOrderCount === 0 && notInOrderLines.length > 0 && !showNotInOrder && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 4px" }}>
              {t(
                "Nothing to order — stock covers predicted demand.",
                "Τίποτα προς παραγγελία — το απόθεμα καλύπτει τη ζήτηση."
              )}
            </p>
          )}

          {notInOrderLines.length > 0 && (
            <button
              type="button"
              className="procureiq-zero-suggest-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowNotInOrder((v) => !v);
              }}
            >
              {showNotInOrder
                ? t(
                    `Hide ${notInOrderLines.length} not in Order`,
                    `Απόκρυψη ${notInOrderLines.length} εκτός παραγγελίας`
                  )
                : t(
                    `Show ${notInOrderLines.length} not in Order`,
                    `Εμφάνιση ${notInOrderLines.length} εκτός παραγγελίας`
                  )}
            </button>
          )}

          <div className="procureiq-line-footer">
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
            >
              ↺ {t("Reset", "Επαναφορά")}
            </button>
            {isBelow && (
              <button
                type="button"
                className="btn-base btn-outline btn-small btn-outline-purple"
                disabled={actionBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  onOverrideMin();
                }}
              >
                ⚠ {t("Override min", "Override min")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
